import React, { useState, useEffect } from 'react';
import { Settings, Database, Globe, CheckCircle, RefreshCw, Camera } from 'lucide-react';
import type { SettingsTabProps } from '../types';
import { api } from '../services/api';

const SettingsTab: React.FC<SettingsTabProps> = ({
  eloKFactor,
  initialRating,
  rounds,
  userComparisons,
  userRole,
}) => {
  const isAdmin = userRole === 'admin';
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const [employeeCount, setEmployeeCount] = useState<number>(0);

  useEffect(() => {
    api.getEmployeeCount().then(setEmployeeCount).catch(() => {});
  }, []);

  // EntraID Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);

  // Photo Sync state
  const [syncingPhotos, setSyncingPhotos] = useState(false);
  const [photoSyncResult, setPhotoSyncResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleSyncEntraIDUsers = async () => {
    setSyncing(true);
    setSyncResult(null);
    setPhotoSyncResult(null);
    try {
      const result = await api.syncEntraIDUsers();
      setSyncResult({ success: true, message: result.message || `Synced successfully. ${result.added ?? 0} added, ${result.updated ?? 0} updated.` });
      api.getEmployeeCount().then(setEmployeeCount).catch(() => {});
    } catch (err) {
      setSyncResult({ success: false, message: err instanceof Error ? err.message : 'Unknown error' });
      console.error('Error syncing EntraID users:', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncPhotos = async () => {
    setSyncingPhotos(true);
    setPhotoSyncResult(null);
    setSyncResult(null);
    try {
      const result = await api.syncPhotos();
      setPhotoSyncResult({ success: true, message: result.message || 'Photo sync completed successfully.' });
    } catch (err) {
      setPhotoSyncResult({ success: false, message: err instanceof Error ? err.message : 'Unknown error' });
      console.error('Error syncing photos:', err);
    } finally {
      setSyncingPhotos(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 sm:gap-3">
        <Settings className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 flex-shrink-0" />
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">System Settings</h2>
      </div>

      {/* Responsive: reduced padding on mobile */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 space-y-6 border border-gray-100">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900">Elo Configuration</h3>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100 opacity-60">
              <label className="block text-sm font-bold text-gray-900 mb-2">
                K-Factor (20-24 recommended)
              </label>
              <input
                type="number"
                value={eloKFactor}
                disabled
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-100 text-gray-400 font-semibold text-lg shadow-sm cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                Higher values make ratings change faster. Default: 21
              </p>
            </div>

            <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100 opacity-60">
              <label className="block text-sm font-bold text-gray-900 mb-2">
                Initial Rating
              </label>
              <input
                type="number"
                value={initialRating}
                disabled
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-100 text-gray-400 font-semibold text-lg shadow-sm cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                Starting Elo rating for all employees. Default: 1500
              </p>
            </div>
          </div>
        </div>

        {process.env.NODE_ENV === 'development' && (
        <div className="border-t-2 border-gray-200 pt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
              <Database className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900">Database Connection</h3>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <p className="text-base text-green-900 font-bold">
                Connected to SQL Server
              </p>
            </div>
            {/* Responsive: stats stack on small mobile */}
            <div className="grid grid-cols-1 xs:grid-cols-3 gap-3 sm:gap-4 mt-4">
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <p className="text-xs text-gray-600 font-medium">Employees</p>
                <p className="text-2xl font-bold text-green-600">{employeeCount}</p>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <p className="text-xs text-gray-600 font-medium">Rounds</p>
                <p className="text-2xl font-bold text-green-600">{rounds.length}</p>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <p className="text-xs text-gray-600 font-medium">Comparisons</p>
                <p className="text-2xl font-bold text-green-600">{userComparisons.length}</p>
              </div>
            </div>
          </div>
        </div>
        )}

        {process.env.NODE_ENV === 'development' && (
        <div className="border-t-2 border-gray-200 pt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900">API Configuration</h3>
          </div>

          <div className="space-y-3 bg-gray-50 rounded-lg p-5 border border-gray-200">
            <div className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
              <span className="text-gray-700 font-medium">API URL:</span>
              <span className="font-mono text-blue-600 font-semibold">{API_BASE_URL}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
              <span className="text-gray-700 font-medium">Authentication:</span>
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">
                Development Mode
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
              <span className="text-gray-700 font-medium">CORS:</span>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                Enabled
              </span>
            </div>
          </div>
        </div>
        )}

        {isAdmin && (
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">EntraID User Sync</h3>
            <p className="text-sm text-gray-600 mb-4">
              Sync employees from EntraID (Azure AD) on demand. This will add new users and update existing ones.
              Photos are synced separately. The automatic scheduler syncs both together.
            </p>
            {/* Responsive: sync buttons wrap on mobile */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleSyncEntraIDUsers}
                disabled={syncing}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors min-h-[44px] ${
                  syncing
                    ? 'bg-blue-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync EntraID Users'}
              </button>
              <button
                onClick={handleSyncPhotos}
                disabled={syncingPhotos}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors min-h-[44px] ${
                  syncingPhotos
                    ? 'bg-purple-400 text-white cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                <Camera className={`w-4 h-4 ${syncingPhotos ? 'animate-pulse' : ''}`} />
                {syncingPhotos ? 'Syncing Photos...' : 'Sync Photos'}
              </button>
            </div>
            {syncResult && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${
                syncResult.success
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}>
                {syncResult.success ? '✓' : '✗'} {syncResult.message}
              </div>
            )}
            {photoSyncResult && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${
                photoSyncResult.success
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}>
                {photoSyncResult.success ? '✓' : '✗'} {photoSyncResult.message}
              </div>
            )}
          </div>
        )}

        {!isAdmin && (
          <div className="border-t-2 border-gray-200 pt-6">
            <p className="text-sm text-gray-500 italic">Settings are read-only for your role.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsTab;
